import _ from 'underscore';
import { DeterministicView, helpers } from '@bingads-webui/deterministic-view';
import $ from 'jquery';

import { DateFilterDropdownView } from '../dropdown/date-filter-dropdown-view';
import { DateFilterPanelsContainer } from '../panels-container/date-filter-panels-container';

import template from './date-filter-template.pug';

const { updateView, ensureChildView } = helpers;

/**
 * The date filter component
 *
 * @class DateFilter
 * @param {Object} [DropdownView] - The view to show the selected date ranges
 * and open or close panels
 * @param {Object} i18n - The i18n component. It should have method formatDate for formatting
 * dates and getString for getting localized strings
 * @param {string} dateFormat - The skeleton date format. Eg: yMd, which corresponds to
 * M/d/y in en-us locale
 * @param {Boolean} isComparisonEnabled - Whether compared date range panel is enabled or not
 * @param {Function} [getDateRanges] - The optional function to get primary date ranges
 * @param {Function} [enabledRangesPredicate] - The optional function to filter primary date ranges
 * It is passed each date range in turn and should return true for the ranges to use
 * @param {Function} [getComparedDateRanges] - The optional function to get compared date ranges
 * @extends {View}
 */
export class DateFilter extends DeterministicView {
  initialize(options) {
    this.options = options;
    this.DropdownView = this.options.DropdownView || DateFilterDropdownView;
    this.customCloseMenuSelector = this.options.datePickerSelector || '.k-calendar';
    $('body').on('click', e => this.closeMenuOnOutsideClick(e));
  }

  closeMenuOnOutsideClick(e) {
    const clickedEl = $(e.target);
    if (!clickedEl.closest('.dropdown-menu', this.$el).length &&
       !clickedEl.closest('.dropdown-toggle', this.$el).length &&
       // click on calender of date picker
       !clickedEl.closest(this.customCloseMenuSelector).length &&
       // click on list of time picker
       !clickedEl.closest('.k-list').length &&
       // click on month selector of date picker
       !clickedEl.closest('.k-content').length
    ) {
      this.$('.dropdown-toggle').parent().removeClass('open');
    }
  }

  get events() {
    return {
      'click .dropdown-toggle': () => this.toggle(),
    };
  }

  /**
   * Sets the state of the date filter
   *
   * @param {Object} newState - The new state of the component
   * @param {Date} newState.today - The date for today to be used in calculating date ranges
   * @param {Object} newState.selection - The date filter preference to use by default
   * @param {Object} newState.selection.primaryRange - The primary range selected
   * @param {Date} newState.selection.primaryRange.start - The primary range start date
   * @param {Date} newState.selection.primaryRange.end - The primary range end date
   * @param {Number} [newState.selection.primaryRange.grain] - The grain for the primary range
   * @param {string} newState.selection.primaryRange.key - The key for the primary range selected
   * @param {Object} [newState.selection.comparedRange] - The compared/PoP range selected
   * @param {Date} newState.selection.comparedRange.start - The compared range start date
   * @param {Date} newState.selection.comparedRange.end - The compared range end date
   * @param {string} newState.selection.comparedRange.key - The key for the compared range
   * @returns {DateFilter} this
   */
  setState({ selection, today }) {
    const datePanelsContainer = ensureChildView(
      this,
      'date-filter-panels-container',
      DateFilterPanelsContainer,
      _.defaults({ today }, _.omit(this.options, 'model')),
      { 'date-range-selected': 'apply' }
    ).setState({
      checkedSelection: selection,
      comparisonToggleChecked: this.options.isComparisonEnabled && selection.comparedRange,
    });

    const { checkedSelection } = datePanelsContainer.getState();
    const appliedSelection = _.mapObject(checkedSelection, range => this.cloneDateRange(range));

    ensureChildView(this, 'dropdown-view', this.DropdownView, {
      i18n: this.options.i18n,
      dateFormat: this.options.dateFormat,
      timeFormat: this.options.timeFormat,
    }).setState({ selection: appliedSelection });

    this.model.set({
      selection: appliedSelection, today,
    });

    return this;
  }

  cloneDateRange(range) {
    if (range) {
      const retValue = _.clone(range);
      const { start, end } = range;
      retValue.start = start ? new Date(start) : start;
      retValue.end = end ? new Date(end) : end;
      return retValue;
    }
    return range;
  }

  /**
   * Apply the selected date filter preference and trigger event
   *
   * @private
   * @param {Object} checkedSelection - The selected date ranges
   * @param {Object} checkedSelection.primaryRange - The primary range selected
   * @param {Date} checkedSelection.primaryRange.start - The primary range start date
   * @param {Date} checkedSelection.primaryRange.end - The primary range end date
   * @param {Number} [checkedSelection.primaryRange.grain] - The grain for the primary range
   * @param {string} checkedSelection.primaryRange.key - The key for the primary range selected
   * @param {Object} [checkedSelection.comparedRange] - The compared/PoP range selected
   * @param {Date} checkedSelection.comparedRange.start - The compared range start date
   * @param {Date} checkedSelection.comparedRange.end - The compared range end date
   * @param {string} checkedSelection.comparedRange.key - The key for the compared range
   * @returns {undefined}
   */
  apply(checkedSelection) {
    const appliedSelection = _.mapObject(checkedSelection, range => this.cloneDateRange(range));
    updateView(this, {
      selection: appliedSelection,
    });
    this.trigger('date-range-selected', appliedSelection);
    this.getChild('dropdown-view').setState({
      selection: appliedSelection,
    });
    this.toggle();
  }

  /**
   * Gets the selected date filter preference
   * @returns {Object} selection - The selected date ranges
   */
  getSelection() {
    const { selection } = this.getState();
    const state = {
      primaryRange: _.pick(selection.primaryRange, 'start', 'end', 'grain', 'key'),
    };
    if (selection.comparedRange) {
      state.comparedRange = _.pick(selection.comparedRange, 'start', 'end', 'key');
    }
    return state;
  }

  /**
   * Toggles the date filter panels visibility
   * @returns {undefined}
   */
  toggle() {
    this.$('.dropdown-toggle').parent().toggleClass('open');
  }

  /**
   * Gets the template for the date filter view
   * @returns {Function} - The template function
   */
  get template() {
    return template;
  }
}
